import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://www.submagic.co/pricing";
const CHECKED = "2026-10-08";

export const SUBMAGIC: CompetitorRecord = {
  slug: "submagic",
  name: "Submagic",
  aliases: ["SubMagic", "Submagic AI", "submagic.co"],
  url: "https://www.submagic.co",
  pricingUrl: PRICING,
  category: "captions",
  positioning:
    "AI short-form editor built around animated captions, now expanding to Magic Clips, scheduling and AI actors.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "3 videos, no credit card",
      includes: [
        "3 free videos, per Submagic's own comparison pages",
        "Watermark on exports, per third-party summaries",
        "No credit card required",
      ],
      sourceUrl: "https://www.submagic.co/vs/veed-io-vs-submagic",
      checkedAt: CHECKED,
      note: "The pricing page says Try for free; the 3-video count is from Submagic's own vs pages and the watermark from G2 summaries.",
    },
    {
      name: "Starter",
      monthly: 19,
      annualPerMonth: 12,
      metered: "45 credits a month, which the page counts as 15 videos, each up to 2 minutes",
      includes: [
        "15 videos a month",
        "2-minute cap per video",
        "1080p at 30fps export",
        "1 seat",
        "API access, included minutes listed as 100 on the card and 10 in the table",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The pricing page's Starter card and its comparison table list different Starter features; the figures here are the ones both agree on.",
    },
    {
      name: "Pro",
      monthly: 39,
      annualPerMonth: 23,
      metered: "120 credits a month, which the page counts as 40 videos, each up to 5 minutes",
      includes: [
        "40 videos a month",
        "5-minute cap per video",
        "1080p on the plan card, 2K in the comparison table",
        "3 seats",
        "Storyblocks B-roll",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The pricing page lists Pro export as 1080p on the plan card and 2K in the comparison table.",
    },
    {
      name: "Business",
      monthly: 69,
      annualPerMonth: 41,
      metered: "300 credits a month, which the page counts as 100 videos, each up to 30 minutes",
      includes: [
        "100 videos a month",
        "30-minute cap per video",
        "4K at 60fps export",
        "Unlimited seats",
        "100 API minutes a month",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Custom",
      custom: true,
      metered: "Custom credits and caps",
      includes: ["Contact sales", "Custom video count and per-video cap", "Custom seats"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Starter",
  entryPlanIncludes: "45 credits, counted as 15 videos of up to 2 minutes each, 1080p at 30fps, 1 seat",
  freeTier: "3 videos with no credit card; third-party summaries report a watermark on free exports",
  trial: "No separate trial; the free account is the try-before-you-buy",
  meteringUnit: "3 credits per video, capped by a monthly video count and a per-video minute limit",
  exportResolution: "1080p at 30fps on Starter, 1080p or 2K on Pro (the page conflicts), 4K at 60fps on Business",
  features: {
    longToShort: { v: "yes", note: "Magic Clips, 20+ clips from one upload", source: "https://www.submagic.co/features/magic-clips" },
    momentRanking: { v: "partial", note: "Claims a virality score, metric not explained" },
    reframing: { v: "n/a", note: "Face tracking not described on Submagic's pages" },
    maxSource: { v: "text", note: "2, 5 or 30 minutes per video by plan", source: PRICING },
    layouts: { v: "n/a", note: "Not enumerated on the pricing page" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "partial", note: "Image and GIF inserts, no generation" },
    voiceover: { v: "partial", note: "AI dubbing only, no text-to-speech listed" },
    voiceCloning: { v: "n/a", note: "Not published" },
    avatars: { v: "yes", note: "AI actors", source: "https://www.submagic.co" },
    timelineEditor: { v: "partial", note: "Text-based cut and trim, auto-edit" },
    captionStyles: { v: "n/a", note: "Template count not published" },
    captionLanguages: { v: "text", note: "123 on the homepage, 100+ on vs pages", source: "https://www.submagic.co" },
    bRoll: { v: "yes", note: "Storyblocks on Pro and up", source: PRICING },
    fourK: { v: "partial", note: "Business only, 4K at 60fps", source: PRICING },
    scheduling: { v: "yes", note: "Social media scheduler", source: "https://www.submagic.co/social-media-scheduler" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "yes", note: "All plans; included minutes conflict on the page", source: PRICING },
    webhooks: { v: "n/a", note: "Not stated" },
    web: { v: "yes" },
    desktop: { v: "no", note: "Own vs page: browser-only", source: "https://www.submagic.co/vs/veed-io-vs-submagic" },
    mobile: { v: "no", note: "Own vs page: browser-only", source: "https://www.submagic.co/vs/veed-io-vs-submagic" },
    teamSeats: { v: "yes", note: "1, 3 or unlimited by plan", source: PRICING },
    freeTier: { v: "yes", note: "3 videos, watermark reported" },
    trial: { v: "text", note: "Free account, no credit card" },
    metering: { v: "text", note: "Credits, video count and minutes per video" },
    rollover: { v: "n/a", note: "Not stated on the pricing page" },
    refunds: { v: "text", note: "Not stated on the pricing page" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the pricing page as of Oct 8, 2026.",
    cancel: "Cancellation steps are not described on the pricing page.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "do not notify you via email of upcoming subscription renewals",
      date: "2026-07-07",
      url: "https://www.trustpilot.com/review/submagic.co?stars=1&stars=2",
      evidences: "cancelInApp",
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: "Pricing page fetched directly: Starter $19/mo or $12/mo annual, Pro $39 or $23, Business $69 or $41, Custom on request; annual totals are not printed, so only per-month annual figures are recorded. The page conflicts with itself in three places that a human should browser-check: API minutes (100 a month on every plan card, 10/10/100 in the comparison table), Pro export (1080p on the card, 2K in the table) and the Starter feature list (card and table differ). The free plan's 3-video count comes from Submagic's own vs pages and the watermark from G2 summaries, not the pricing page.",
  },
  seoPattern: "submagic.co/vs/<a>-vs-<b> and /alternative/<x> in about 20 locales, no page about Crayo",
};
